import firebase from 'firebase';
import { firestore, query, collection, where, getAll } from 'firebase/firestore';
import { db, auth } from '../db';

// Helper Function
function err(e) {
  return { success: false, error: e };
}

// Users.vue
/*
  Get all Testers and Developers.
  returns a promise with an array of User data.
*/
async function getAllUsers() {
  return new Promise((resolve, reject) => {
    Promise.all([
      db.collection('User').where('userType', '!=', 'admin').get(),
      db.collection('Game').get(),
    ])
      .then(([querySnapshot, gameSnapshot]) => {
        if (querySnapshot.empty) {
          resolve({ success: true, data: [] }); /* No testers or developers in the DB */
        } else {
          const allGames = gameSnapshot.docs.map((doc) => ({
            id: doc.id,
            gameName: doc.data().gameName || 'Untitled project',
            developerID: doc.data().developerID,
            testerIDs: doc.data().testerIDs || [],
          }));

          const users = querySnapshot.docs.map((doc) => {
            const gameList = matchUserGames(allGames, doc.id, doc.data().userType); /* Using helper function */
            return {
              id: doc.id,
              userName: doc.data().userName,
              userType: doc.data().userType,
              nickName: doc.data().nickName,
              bio: doc.data().bio,
              gender: Array.isArray(doc.data().gender)
                ? doc.data().gender.join(', ') || undefined
                : doc.data().gender,
              gameIds: gameList.map((g) => g.id),
              games: gameList,
              email: doc.data().email,
            };
          });

          resolve({ success: true, data: users });
        }
      })
      .catch((e) => reject(err(e)));
  });
}

function matchUserGames(allGames, userID, userType) {
  return allGames
    .filter((game) => {
      if (userType === 'tester') return game.testerIDs.includes(userID);
      if (userType === 'developer') return game.developerID === userID;
      return false;
    })
    .map((game) => ({
      id: game.id,
      gameName: game.gameName,
      testerCount: game.testerIDs.length,
    }));
}

/*
  Helper function used in getAllUsers.
  returns a promise with an array of gameIDs relevant to the input user.
  userType must be either tester or developer.
*/
async function getUserGames(userID, userType) {
  return new Promise(async (resolve, reject) => {
    /* Get all games assigned to this tester */
    if (userType === 'tester') {
      db.collection('Game')
        .where('testerIDs', 'array-contains', userID)
        .get()
        .then((games) => {
          let gameList = [];
          games.forEach((doc) => {
            gameList.push({
              id: doc.id,
              gameName: doc.data().gameName || 'Untitled project',
              testerCount: (doc.data().testerIDs || []).length,
            });
          });
          resolve({ success: true, data: gameList });
        })
        .catch((e) => reject(err(e)));
    }

    /* Get all games developed by this developer */
    if (userType === 'developer') {
      db.collection('Game')
        .where('developerID', '==', userID)
        .get()
        .then((games) => {
          let gameList = [];
          games.forEach((doc) => {
            gameList.push({
              id: doc.id,
              gameName: doc.data().gameName || 'Untitled project',
              testerCount: (doc.data().testerIDs || []).length,
            });
          });
          resolve({ success: true, data: gameList });
        })
        .catch((e) => reject(err(e)));
    }

    if (userType !== 'tester' && userType !== 'developer') {
      resolve({ success: true, data: [] });
    }
  });
}

/*
  Update a specific user's data in Firestore.
  Takes a userID and the updated data as input.
  Returns a promise indicating success or failure.
*/
async function updateUser(userID, updatedData) {
  return new Promise((resolve, reject) => {
    db.collection('User')
      .doc(userID)
      .update(updatedData)
      .then(() => {
        resolve({ success: true, message: 'User updated successfully' });
      })
      .catch((error) => {
        reject(err(error));
      });
  });
}

export default {
  getAllUsers,
  getUserGames,
  updateUser, // Added this function to the export
};



// WEBPACK FOOTER //
// ./src/Firestore/UserCollection.js